import type { createGameKit, JsonValue, Scope } from "@e308/core";
import { initialCombatState, validateCombatState } from "./combat-state.js";
import { equalAllocations, INITIAL_TOWN } from "./content/setup.js";
import type { EconomySettings, SeatModel, SeatResources } from "./content-types.js";
import { validateEconomySettings } from "./definition-validation.js";
import { buildingRule, GUARD_ROBOT, HEADQUARTERS } from "./economy-rules.js";
import {
  ECONOMY_BUILDINGS,
  FACTORY_WORKS_OUTPUTS,
  METAL_LANES,
  POWER_DEPARTMENTS,
} from "./economy-types.js";
import type { Micro } from "./exact-numbers.js";
import { initialLogisticsState, validateLogisticsState } from "./logistics-state.js";
import { initialManufacturingState, validateManufacturingState } from "./manufacturing-state.js";
import { initialResearchState, validateResearchState } from "./research-state.js";
import type { LockedSeat } from "./types.js";

export function createSeatModel(
  kit: ReturnType<typeof createGameKit<Micro>>,
  seat: LockedSeat,
  seatIds: readonly string[],
): SeatModel {
  const borderIds = Object.freeze(seatIds.filter((id) => id !== seat.id).sort());
  const scope = kit.scope(seat.id);
  const resources = createSeatResources(kit, seat, scope);
  const powerOperation = kit.allocation(`${seat.id}/power-operation`, {
    scope,
    budget: resources.percentageBudget,
    targets: POWER_DEPARTMENTS,
    initial: Object.fromEntries(POWER_DEPARTMENTS.map((id) => [id, kit.q("100")])),
  });
  const factoryWorksSplit = kit.allocation(`${seat.id}/factory-works-split`, {
    scope,
    budget: resources.percentageBudget,
    targets: FACTORY_WORKS_OUTPUTS,
    initial: {
      "rig-factory": kit.q("100"),
      "yard-factory": kit.q("0"),
      "robot-factory": kit.q("0"),
    },
  });
  const constructionSplit = kit.allocation(`${seat.id}/construction-split`, {
    scope,
    budget: resources.targetingBudget,
    targets: ["new-construction", "restoration"],
    initial: { "new-construction": kit.q("100"), restoration: kit.q("0") },
  });
  const targeting = createTargetingAllocation(kit, seat, borderIds, scope, resources);
  const supplyAllocation = createSupplyAllocation(kit, seat, borderIds, scope, resources);
  const shieldAllocation = createBorderAllocation(
    kit,
    seat,
    borderIds,
    scope,
    resources,
    "shield-allocation",
  );
  const records = createSeatRecords(kit, seat, borderIds, scope);
  return Object.freeze({
    seat,
    borderIds,
    scope,
    resources,
    powerOperation,
    factoryWorksSplit,
    constructionSplit,
    targeting,
    supplyAllocation,
    shieldAllocation,
    ...records,
  });
}

function createSeatRecords(
  kit: ReturnType<typeof createGameKit<Micro>>,
  seat: LockedSeat,
  borderIds: readonly string[],
  scope: Scope,
) {
  const initial: EconomySettings & JsonValue = {
    powerPriority: [...POWER_DEPARTMENTS],
    metalPriority: [...METAL_LANES],
  };
  return Object.freeze({
    economySettings: kit.record(`${seat.id}/economy-settings`, {
      scope,
      version: 1,
      initial,
      validate: validateEconomySettings,
    }),
    manufacturing: kit.record(`${seat.id}/manufacturing`, {
      scope,
      version: 1,
      initial: initialManufacturingState(),
      validate: validateManufacturingState,
    }),
    research: kit.record(`${seat.id}/research`, {
      scope,
      version: 1,
      initial: initialResearchState(),
      validate: validateResearchState,
    }),
    logistics: kit.record(`${seat.id}/logistics`, {
      scope,
      version: 1,
      initial: initialLogisticsState(borderIds),
      validate: (value) => validateLogisticsState(value, borderIds),
    }),
    combat: kit.record(`${seat.id}/combat`, {
      scope,
      version: 1,
      initial: initialCombatState(borderIds),
      validate: (value) => validateCombatState(value, borderIds),
    }),
  });
}

function createTargetingAllocation(
  kit: ReturnType<typeof createGameKit<Micro>>,
  seat: LockedSeat,
  borderIds: readonly string[],
  scope: Scope,
  resources: SeatResources,
) {
  const firstTarget = seat.adjacentTo[0];
  if (!firstTarget) throw new TypeError(`Seat ${seat.id} has no adjacent target`);
  return kit.allocation(`${seat.id}/targeting`, {
    scope,
    budget: resources.targetingBudget,
    targets: borderIds,
    initial: Object.fromEntries(
      borderIds.map((id) => [id, kit.q(id === firstTarget ? "100" : "0")]),
    ),
  });
}

function createSupplyAllocation(
  kit: ReturnType<typeof createGameKit<Micro>>,
  seat: LockedSeat,
  borderIds: readonly string[],
  scope: Scope,
  resources: SeatResources,
) {
  return createBorderAllocation(kit, seat, borderIds, scope, resources, "supply-allocation");
}

function createBorderAllocation(
  kit: ReturnType<typeof createGameKit<Micro>>,
  seat: LockedSeat,
  borderIds: readonly string[],
  scope: Scope,
  resources: SeatResources,
  id: "supply-allocation" | "shield-allocation",
) {
  const initial = equalAllocations(seat.adjacentTo);
  return kit.allocation(`${seat.id}/${id}`, {
    scope,
    budget: resources.targetingBudget,
    targets: borderIds,
    initial: Object.fromEntries(borderIds.map((id) => [id, kit.q(String(initial[id] ?? 0))])),
  });
}

function createSeatResources(
  kit: ReturnType<typeof createGameKit<Micro>>,
  seat: LockedSeat,
  scope: Scope,
): SeatResources {
  const resource = (name: string, initial: string, capacity?: string) =>
    kit.resource(`${seat.id}/${name}`, {
      scope,
      initial: kit.q(initial),
      ...(capacity ? { capacity: kit.q(capacity), overflow: "clamp" as const } : {}),
    });
  return Object.freeze({
    metal: resource("metal", String(INITIAL_TOWN.metal)),
    researchPoints: resource("research-points", String(INITIAL_TOWN.researchPoints)),
    miningRig: resource("mining-rig", initialBuilding("mining-rig")),
    powerGenerator: resource("power-generator", initialBuilding("power-generator")),
    constructionYard: resource("construction-yard", initialBuilding("construction-yard")),
    rigFactory: resource("rig-factory", initialBuilding("rig-factory")),
    yardFactory: resource("yard-factory", initialBuilding("yard-factory")),
    factoryWorks: resource("factory-works", initialBuilding("factory-works")),
    robotFactory: resource("robot-factory", initialBuilding("robot-factory")),
    researchLab: resource("research-lab", initialBuilding("research-lab")),
    shieldGenerator: resource("shield-generator", initialBuilding("shield-generator")),
    supplyDepot: resource("supply-depot", initialBuilding("supply-depot")),
    guardRobot: resource("guard-robot", String(GUARD_ROBOT.initialReserve)),
    raiderRobot: resource("raider-robot", "0"),
    siegeGun: resource("siege-gun", "0"),
    wreckage: Object.freeze(
      Object.fromEntries(ECONOMY_BUILDINGS.map((id) => [id, resource(`wreckage/${id}`, "0")])),
    ) as SeatResources["wreckage"],
    headquarters: resource(
      "headquarters",
      String(HEADQUARTERS.initialIntegrity),
      String(HEADQUARTERS.initialIntegrity),
    ),
    percentageBudget: resource("percentage-budget", "400", "400"),
    targetingBudget: resource("targeting-budget", "100", "100"),
  });
}

function initialBuilding(id: Parameters<typeof buildingRule>[0]): string {
  return String(buildingRule(id).initial);
}
